import { Attributes, System, SystemQueries } from "ecsy";
import { ConstraintData } from "../DataComponents/ConstraintData";
import { MassData } from "../DataComponents/MassData";
import { TransformData } from "../DataComponents/TransformData";
import { VelocityData } from "../DataComponents/VelocityData";
import { Vector2 } from "../Math/Vector2";
import { DraggableTag } from "../TagComponents/DraggableTag";

const maxForce = 10000;
const maxVelocity = 50000;

export class ConstraintPhysicsSystem extends System {
  static queries: SystemQueries = {
    constraints: {
      components: [ConstraintData, MassData, VelocityData, TransformData],
    },
  };

  execute(delta: number, time: number): void {
    this.queries.constraints.results.forEach((entity) => {
      let constraint = entity.getComponent(ConstraintData);
      let transform = entity.getMutableComponent(TransformData);
      let mass = entity.getComponent(MassData);
      let velocity = entity.getMutableComponent(VelocityData);

      let neighborConstraints = constraint.neighbors;
      let constraintForce = constraint.constraintForce;

      // Calculate the total force applied to the constraint.
      let totalForce: Vector2 = new Vector2(0, 0);

      // Calculate neighbor forces.
      neighborConstraints.forEach((neighborConstraint) => {
        let neighborTransform =
          neighborConstraint.target.getMutableComponent(TransformData);

        // Calculate the force applied to the constraint.
        let distanceDiff =
          neighborTransform.position.sub(transform.position).magnitude() -
          neighborConstraint.distance;

        let forceMagnitude = distanceDiff * constraintForce;
        // Clamp the force magnitude to >= 0.
        forceMagnitude = Math.max(forceMagnitude, 0);

        let forceDirection = neighborTransform.position
          .sub(transform.position)
          .normalize();

        let force = forceDirection.scale(forceMagnitude);

        // Add the force to the total force.
        totalForce = totalForce.add(force);
      });

      // Clamp the total force to the max force.
      if (totalForce.magnitude() > maxForce) {
        totalForce = totalForce.normalize().scale(maxForce);
      }

      // Calculate the acceleration.
      let acceleration = totalForce.scale(1 / mass.mass);

      // Calculate the new velocity.
      velocity.velocity = velocity.velocity.add(acceleration.scale(delta));

      // Clamp the velocity to the max velocity.
      if (velocity.velocity.magnitude() > maxVelocity) {
        velocity.velocity = velocity.velocity.normalize().scale(maxVelocity);
      }

      // Decrement the velocity by the drag.
      velocity.velocity = velocity.velocity.scale(0.97);
    });
  }
}
